// One memoised loader for the product page chunk.
//
// The keyword-route resolver renders it, the client entry warms it before
// mount when the SSR state says the page is a product, and the layout warms it
// on a product-card click. All three share this single `import()` call site:
// the 2026-10-01 performance audit warned that a second, independent
// `import()` of product.vue can resolve before its route CSS under Vite 2.9
// and show one unstyled PDP frame.
type ProductPageModule =
  typeof import("~/core/components/pages/store/product/product.vue");

let pending: Promise<ProductPageModule> | null = null;

export function loadStoreProduct(): Promise<ProductPageModule> {
  if (!pending) {
    pending = import("~/core/components/pages/store/product/product.vue").catch(
      (error) => {
        // A failed chunk request (offline, deploy in progress) must not poison
        // later navigations; the next caller retries.
        pending = null;
        throw error;
      }
    );
  }
  return pending;
}

// Fire-and-forget warm-up. Errors surface later through loadStoreProduct()
// when the page actually renders.
export function preloadStoreProduct(): void {
  if (typeof window === "undefined") return;
  loadStoreProduct().catch(() => undefined);
}

// The SSR state is an inline script that runs before the entry module, so the
// product route is known before the app is even created.
export function isInitialProductDocument(
  syncState: unknown,
  pathname: string
): boolean {
  const route = (
    syncState as {
      vuex?: {
        vuefront?: {
          resolvedSeoRoute?: {
            type?: unknown;
            id?: unknown;
            path?: unknown;
            notFound?: unknown;
          };
        };
      };
    } | null
  )?.vuex?.vuefront?.resolvedSeoRoute;
  if (!route || route.type !== "product" || !route.id || route.notFound) {
    return false;
  }
  return !route.path || route.path === pathname;
}
